import {
  Button,
  ExportButton,
  Grid,
  Icons,
  Image,
  Input,
  List,
  Modal,
  Select,
  Space,
  Table,
  TableProps,
  Tag,
  Typography,
  notification,
} from '@pankod/refine-antd';
import { IResourceComponentsProps, useCan, useNavigation } from '@pankod/refine-core';
import { useSearchParams } from '@pankod/refine-react-router-v6';
import defaultAvatar from 'assets/images/default-avatar.jpg';
import { SearchNew } from 'components';
import CustomTableWithPagination from 'components/table/custom-table';
import { MobileDataCard } from 'components/table/mobile-data-card';
import { MobileInfiniteVirtualList } from 'components/table/mobile-infinite-virtual-list';
import { ISort, PaginationHandler } from 'interfaces';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  IInventoryLotResponse,
  InventoriesQueryParams,
  getInventories,
  getInventoriesDropdown,
  updateInventoryCopyMinUnit,
} from 'services/inventory';
import { SearchIndex } from 'services/meilisearch';
import { getWarehouses } from 'services/warehouse';
import { useFeatureFlag } from 'hooks/useFeatureFlags';
import { downloadCSVFile } from 'utilities/file';
import MetaData from 'utilities/metadata';
import { capitalize, formatCurrency } from 'utilities/shared';
import {
  warehouseQuantityColumns,
  warehouseQuantityFor,
} from 'utilities/warehouse-quantity-columns';
import styles from './style.module.css';

const { Text } = Typography;
const { Option } = Select;

interface IWarehouseOption {
  id: string;
  name: string;
}

export const InventoryList: React.FC<IResourceComponentsProps> = () => {
  const breakpoint = Grid.useBreakpoint();
  const isMobile = !breakpoint.sm;
  const fetchRequestIdRef = useRef(0);
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>('All');
  // Stitches S5: on Inventory All, one stock column per warehouse.
  const isAllShowroomColumnsEnabled = useFeatureFlag('inventory.allShowroomColumns');
  const showWarehouseQuantities = isAllShowroomColumnsEnabled && selectedWarehouse === 'All';
  const [warehouseOptions, setWarehouseOptions] = useState<IWarehouseOption[]>([]);
  const [inventories, setInventories] = useState<IInventoryLotResponse[]>([]);
  const warehouseColumns = useMemo(
    () => (showWarehouseQuantities ? warehouseQuantityColumns(inventories) : []),
    [inventories, showWarehouseQuantities],
  );
  const [totalInventories, setTotalInventories] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [sort, setSort] = useState<ISort | null>(null);
  const [isExportButtonLoading, setIsExportButtonLoading] = useState<boolean>(false);
  const [isMinUnitModalVisible, setIsMinUnitModalVisible] = useState<boolean>(false);
  const [selectedInventoryForMinUnit, setSelectedInventoryForMinUnit] =
    useState<IInventoryLotResponse | null>(null);
  const [minUnitInputValue, setMinUnitInputValue] = useState<string>('');
  const [minUnitInputError, setMinUnitInputError] = useState<string>('');
  const [isSavingMinUnit, setIsSavingMinUnit] = useState<boolean>(false);

  const [productTypeFilter, setProductTypeFilter] = useState<('product' | 'material')[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [searchParams, _] = useSearchParams();
  const { push } = useNavigation();

  const { data: readPurchasePrice } = useCan({
    resource: 'purchase_price',
    action: 'read',
  });
  const categorySearchParam = searchParams.get('category');
  const filterOptions = useMemo(() => {
    return categorySearchParam
      ? [
          {
            field: 'categories',
            value: categorySearchParam,
          },
        ]
      : undefined;
  }, [categorySearchParam]);

  useEffect(() => {
    (async () => {
      try {
        const warehouseResponse = await getWarehouses();
        const warehouseData = warehouseResponse.data;
        const requiredWarehouseData = warehouseData.map((warehouse) => ({
          id: warehouse.id,
          name: warehouse.name,
        }));
        const warehouseOptions = [{ id: 'All', name: 'All' }].concat(requiredWarehouseData);
        setWarehouseOptions(warehouseOptions);
      } catch (err: any) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
    })();
  }, []);

  const fetchDataOnMountCallback = useCallback(async () => {
    const requestId = ++fetchRequestIdRef.current;
    const shouldAppend = isMobile && page > 1;

    if (shouldAppend) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }

    try {
      const query: InventoriesQueryParams = {
        searchString: searchText,
        start: page,
        filterOptions,
        productType: productTypeFilter[0],
        sort,
        limit,
      };
      if (selectedWarehouse !== 'All') query.warehouse = selectedWarehouse;
      if (showWarehouseQuantities) query.includeWarehouseQuantities = true;

      const inventoryResponse = await getInventories(query);

      if (requestId !== fetchRequestIdRef.current) {
        return;
      }

      setInventories((previous) =>
        shouldAppend ? previous.concat(inventoryResponse.data) : inventoryResponse.data,
      );
      setTotalInventories(inventoryResponse.total);
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    }
  }, [
    filterOptions,
    isMobile,
    limit,
    page,
    productTypeFilter,
    searchText,
    selectedWarehouse,
    showWarehouseQuantities,
    sort,
  ]);

  useEffect(() => {
    fetchDataOnMountCallback();
  }, [fetchDataOnMountCallback]);

  useEffect(() => {
    if (!isMobile) {
      return;
    }

    setPage(1);
    setInventories([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterOptions, isMobile, productTypeFilter, searchText, selectedWarehouse, sort]);

  const onSelectWarehouseOption = (value: string) => {
    setSelectedWarehouse(value);
  };

  const onSelectSearchOption = async (searchText: string) => {
    setSearchText(searchText);
    setPage(1);
  };

  const onResetSearch = useCallback(() => {
    setSearchText('');
    setPage(1);
  }, []);

  const getRowKey = (inventory: IInventoryLotResponse) => {
    return inventory.productVariant.id;
  };

  const onExportButtonClick = async () => {
    setIsExportButtonLoading(true);
    try {
      const warehouseId = selectedWarehouse !== 'All' ? selectedWarehouse : undefined;
      const inventoryData = await getInventoriesDropdown(
        warehouseId,
        sort,
        filterOptions,
        searchText,
        productTypeFilter[0],
        showWarehouseQuantities,
      );
      const exportWarehouseColumns = warehouseQuantityColumns(inventoryData);

      const canReadPrice = readPurchasePrice?.can;

      const headers = ['SKU', 'Name', 'tags'];
      if (canReadPrice) {
        headers.push('Unit Price', 'Latest Purchase Price');
      }
      headers.push('Units');
      exportWarehouseColumns.forEach((column) => headers.push(column.title));
      headers.push('Purchased Stock Value', 'Current Stock Value');

      const rows = [headers];
      let totalUnits = 0;
      let totalPurchasedStockValue = 0;
      let totalCurrentStockValue = 0;

      inventoryData.forEach((element) => {
        totalUnits += element.quantity;
        totalPurchasedStockValue += element.totalStockValue;
        totalCurrentStockValue += element.quantity * element.productVariant.purchasePrice;

        const row = [
          String.raw`'${element.productVariant.sku}'`,
          JSON.stringify(element.productVariant.name),
          `${JSON.stringify(element.productVariant.productTags)}`,
        ];

        if (canReadPrice) {
          row.push(element.unitPrice.toString(), element.productVariant.purchasePrice.toString());
        }

        row.push(element.quantity.toString());
        exportWarehouseColumns.forEach((column) =>
          row.push(warehouseQuantityFor(element, column).toString()),
        );
        row.push(
          element.totalStockValue.toString(),
          JSON.stringify(element.quantity * element.productVariant.purchasePrice),
        );

        rows.push(row);
      });

      rows.push([]);

      const footerRow = ['', '', '']; // Spacers for SKU, Name, tags
      if (canReadPrice) {
        footerRow.push('', ''); // Spacers for Unit Price, Latest Purchase Price
      }

      footerRow.push(`Total Units = ${totalUnits.toString()}`);
      exportWarehouseColumns.forEach(() => footerRow.push(''));
      footerRow.push(
        `Total Purchased Stock Value = ${totalPurchasedStockValue.toString()}`,
        `Total Current Stock Value = ${totalCurrentStockValue.toString()}`,
      );

      rows.push(footerRow);

      downloadCSVFile(rows);
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
    setIsExportButtonLoading(false);
  };

  const pageHeaders = (
    <ExportButton loading={isExportButtonLoading} onClick={onExportButtonClick} />
  );

  const onTableChange: TableProps<IInventoryLotResponse>['onChange'] = async (
    pagination,
    filters,
    sorter,
    _extra,
  ) => {
    // Sort Handling
    if (sorter && 'field' in sorter && sorter.field && sorter.order) {
      const mappedField = Array.isArray(sorter.field) ? sorter.field.at(-1) : sorter.field;
      const mappedOrder = sorter.order === 'ascend' ? 'ASC' : 'DESC';

      if (!sort || sort.field !== mappedField || sort.order !== mappedOrder) {
        setSort({
          // @ts-ignore
          field: mappedField,
          order: mappedOrder,
        });
      }
    } else if (sort) {
      setSort(null);
    }

    // Filter Handling
    const typeFilter = (filters['productVariant.type'] as ('product' | 'material')[]) ?? [];
    if (JSON.stringify(typeFilter) !== JSON.stringify(productTypeFilter)) {
      setProductTypeFilter(typeFilter);
    }

    // Pagination Handling
    const newPage = pagination.current ?? 1;
    const newLimit = pagination.pageSize ?? 10;

    if (newPage !== page) {
      setPage(newPage);
    }

    if (newLimit !== limit) {
      setLimit(newLimit);
    }
  };

  const onPageChange: PaginationHandler = async (page: number, pageSize: number) => {
    setPage(page);
    setLimit(pageSize);
  };

  const getTagValue = (value: string) => {
    if (!value) return null;

    return (
      <>
        {value.split(',').map((splitValue: string, index: number) => {
          return (
            <Tag style={{ margin: 2 }} key={`${index}-${splitValue}`}>
              {splitValue.trim().toUpperCase()}
            </Tag>
          );
        })}
      </>
    );
  };

  const getShowEndpoint = useCallback(
    (record: IInventoryLotResponse) => {
      const warehouseId = selectedWarehouse !== 'All' ? selectedWarehouse : null;
      const query = warehouseId ? `?warehouse=${warehouseId}` : '';
      const encodedProductId = encodeURIComponent(record.productVariant.id);
      return `show/${encodedProductId}${query}`;
    },
    [selectedWarehouse],
  );

  const renderRowActions = useCallback(
    (record: IInventoryLotResponse): React.ReactNode => {
      return (
        <Space onClick={(event) => event.stopPropagation()}>
          <Button
            size="small"
            onClick={() => push(getShowEndpoint(record))}
            icon={<Icons.EyeOutlined />}
          />
        </Space>
      );
    },
    [getShowEndpoint, push],
  );

  const loadMoreInventories = useCallback(() => {
    if (!isMobile || isLoading || isLoadingMore) {
      return;
    }

    if (inventories.length >= totalInventories) {
      return;
    }

    setIsLoadingMore(true);
    setPage((previous) => previous + 1);
  }, [inventories.length, isLoading, isLoadingMore, isMobile, totalInventories]);

  const openMinUnitModal = useCallback((record: IInventoryLotResponse) => {
    setSelectedInventoryForMinUnit(record);
    setMinUnitInputValue(
      record.minUnit === null || record.minUnit === undefined ? '' : String(record.minUnit),
    );
    setMinUnitInputError('');
    setIsMinUnitModalVisible(true);
  }, []);

  const closeMinUnitModal = useCallback(() => {
    setIsMinUnitModalVisible(false);
    setSelectedInventoryForMinUnit(null);
    setMinUnitInputValue('');
    setMinUnitInputError('');
  }, []);

  const saveMinUnit = useCallback(async () => {
    if (!selectedInventoryForMinUnit) return;
    if (selectedWarehouse === 'All') return;
    if (!selectedInventoryForMinUnit.id) {
      notification.error({
        message: 'Failed',
        description: 'Inventory identifier missing for selected warehouse row.',
      });
      return;
    }
    const inputValue = minUnitInputValue.trim();
    if (!inputValue) {
      setMinUnitInputError('');
      setIsSavingMinUnit(true);
    } else if (!/^\d+$/.test(inputValue) || Number(inputValue) < 0) {
      setMinUnitInputError('Please input a valid stock unit');
      return;
    } else {
      setMinUnitInputError('');
      setIsSavingMinUnit(true);
    }

    const minUnit = inputValue ? Number(inputValue) : null;
    try {
      await updateInventoryCopyMinUnit({
        inventoryCopyId: selectedInventoryForMinUnit.id,
        minUnit,
      });
      await fetchDataOnMountCallback();
      notification.success({
        message: 'Success',
        description: 'Minimum unit updated successfully.',
      });
      closeMinUnitModal();
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    } finally {
      setIsSavingMinUnit(false);
    }
  }, [
    closeMinUnitModal,
    fetchDataOnMountCallback,
    minUnitInputValue,
    selectedInventoryForMinUnit,
    selectedWarehouse,
  ]);

  return (
    <List title="Inventory" headerProps={{ extra: pageHeaders }}>
      <MetaData />
      <section className={styles['filter-section-list']}>
        <Select
          showSearch
          className={styles['filter-section-list__selector']}
          size="large"
          defaultValue="All"
          filterOption={(input, option: any) =>
            option!.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
          onSelect={onSelectWarehouseOption}
        >
          {warehouseOptions.map((warehouse) => (
            <Option key={warehouse.id} value={warehouse.id}>
              {warehouse.name}
            </Option>
          ))}
        </Select>

        <SearchNew
          placeholder="Search by SKU, Name, Tags, Category"
          size="large"
          className={styles['filter-section__search']}
          index={SearchIndex.ProductVariant}
          groups={['sku', 'name', 'tag', 'category']}
          onSelectOption={onSelectSearchOption}
          onReset={onResetSearch}
        />
      </section>

      {categorySearchParam && (
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
          <Tag
            color="blue"
            closable
            onClose={() => {
              const url = `/inventories`;
              push(url);
            }}
          >
            Category: {categorySearchParam}
          </Tag>
        </div>
      )}

      {isMobile ? (
        <div className="soft-list-section mobile-card-page">
          <MobileInfiniteVirtualList
            data={inventories}
            total={totalInventories}
            isLoadingInitial={isLoading}
            isLoadingMore={isLoadingMore}
            loadMore={loadMoreInventories}
            itemGap={0}
            renderItem={(record) => (
              <div>
                <MobileDataCard
                  image={
                    <img
                      src={record.productVariant.avatar || defaultAvatar}
                      alt={record.productVariant.name}
                      loading="lazy"
                    />
                  }
                  title={record.productVariant.name}
                  skuValue={record.productVariant.sku}
                  skuCopyText={record.productVariant.sku}
                  secondaryText={`Units: ${record.quantity}`}
                  sellingPrice={record.productVariant.sellingPrice}
                  actions={[
                    {
                      key: 'view',
                      icon: <Icons.EyeOutlined />,
                      label: 'View',
                      onClick: () => push(getShowEndpoint(record)),
                    },
                  ]}
                />
              </div>
            )}
          />
        </div>
      ) : (
        <CustomTableWithPagination
          total={totalInventories}
          page={page}
          limit={limit}
          onPageChange={onPageChange}
        >
          <Table
            dataSource={inventories}
            loading={isLoading}
            rowKey={getRowKey}
            scroll={{ x: true }}
            onChange={onTableChange}
            // pagination={{
            //   defaultPageSize: 10,
            //   showSizeChanger: true,
            //   total: totalInventories,
            //   current: page,
            // }}
            pagination={false}
          >
            <Table.Column<IInventoryLotResponse>
              dataIndex={['productVariant', 'sku']}
              title="SKU"
              sorter={(a, b) => {
                if (a.productVariant.sku < b.productVariant.sku) return -1;
                if (a.productVariant.sku > b.productVariant.sku) return 1;
                return 0;
              }}
            />
            <Table.Column<IInventoryLotResponse>
              dataIndex="avatar"
              title="Image"
              width="6%"
              render={(_text, record): React.ReactNode => {
                return (
                  <Image
                    src={record?.productVariant?.avatar || defaultAvatar}
                    style={{ objectFit: 'cover' }}
                    height={45}
                    width={45}
                    alt={record.productVariant.name}
                  />
                );
              }}
            />
            <Table.Column<IInventoryLotResponse>
              dataIndex={['productVariant', 'name']}
              title="Name"
              sorter={(a, b) => {
                if (a.productVariant.name < b.productVariant.name) return -1;
                if (a.productVariant.name > b.productVariant.name) return 1;
                return 0;
              }}
            />
            <Table.Column<IInventoryLotResponse>
              dataIndex={['productVariant', 'type']}
              title="type"
              filteredValue={productTypeFilter}
              filterMultiple={false}
              filters={[
                {
                  text: 'Product',
                  value: 'product',
                },
                {
                  text: 'Material',
                  value: 'material',
                },
              ]}
              render={(value) => {
                if (!value) return null;
                return <Tag key={value}>{capitalize(value)}</Tag>;
              }}
            />
            <Table.Column<IInventoryLotResponse>
              dataIndex={['productVariant', 'productTags']}
              title="Tags"
              render={(value) => {
                return getTagValue(value);
              }}
            />
            <Table.Column<IInventoryLotResponse>
              dataIndex={['productVariant', 'productCategories']}
              title="Category"
              render={(value) => {
                return value ? <Tag color="blue">{value.trim().toUpperCase()}</Tag> : null;
              }}
            />
            {readPurchasePrice?.can && (
              <Table.Column<IInventoryLotResponse>
                dataIndex="unitPrice"
                title="Latest Purchase Price"
                sorter={(a, b) => a.unitPrice - b.unitPrice}
              />
            )}
            <Table.Column<IInventoryLotResponse>
              dataIndex="quantity"
              title="Units"
              render={(_, record): React.ReactNode => (
                <StockQuantity
                  record={record}
                  enableLowStockHighlight={selectedWarehouse !== 'All'}
                />
              )}
              sorter={(a, b) => a.quantity - b.quantity}
            />
            {warehouseColumns.map((column) => (
              <Table.Column<IInventoryLotResponse>
                key={column.key}
                title={column.title}
                render={(_, record) => warehouseQuantityFor(record, column)}
              />
            ))}
            {selectedWarehouse !== 'All' && (
              <Table.Column<IInventoryLotResponse>
                title="Min Unit"
                render={(_, record) => (
                  <Space size="small">
                    <Text>
                      {record.minUnit === null || record.minUnit === undefined
                        ? '-'
                        : record.minUnit}
                    </Text>
                    <Button
                      size="small"
                      type="text"
                      icon={<Icons.EditOutlined />}
                      onClick={() => openMinUnitModal(record)}
                    />
                  </Space>
                )}
              />
            )}
            <Table.Column<IInventoryLotResponse>
              dataIndex="totalStockValue"
              title="Purchased Stock Value"
              render={(_, record) => formatCurrency(record.totalStockValue)}
              // sorter={(a, b) => a.totalStockValue - b.totalStockValue}
            />
            <Table.Column<IInventoryLotResponse>
              title="Current Stock Value"
              render={(_, record) =>
                formatCurrency(record.quantity * record.productVariant.purchasePrice)
              }
            />
            <Table.Column<IInventoryLotResponse>
              title="Actions"
              dataIndex="actions"
              render={(_, record): React.ReactNode => renderRowActions(record)}
            />
          </Table>
        </CustomTableWithPagination>
      )}
      <Modal
        title="Set Minimum Unit"
        visible={isMinUnitModalVisible}
        onCancel={closeMinUnitModal}
        onOk={saveMinUnit}
        okText="Save"
        confirmLoading={isSavingMinUnit}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Text strong>{selectedInventoryForMinUnit?.productVariant?.name}</Text>
          <Text type="secondary">Current stock: {selectedInventoryForMinUnit?.quantity ?? 0}</Text>
          <Input
            style={{ width: '100%' }}
            placeholder="Enter minimum unit"
            value={minUnitInputValue}
            onChange={(event) => setMinUnitInputValue(event.target.value)}
            onBlur={(event) => {
              const inputValue = event.target.value.trim();
              if (!inputValue) {
                setMinUnitInputError('');
                return;
              }
              if (!/^\d+$/.test(inputValue) || Number(inputValue) < 0) {
                setMinUnitInputError('Please input a valid stock unit');
                return;
              }
              setMinUnitInputError('');
            }}
          />
          {!!minUnitInputError && <Text type="danger">{minUnitInputError}</Text>}
          <Button
            onClick={() => {
              setMinUnitInputValue('');
              setMinUnitInputError('');
            }}
          >
            Clear threshold
          </Button>
        </Space>
      </Modal>
    </List>
  );
};

type StockQuantityProps = {
  record: IInventoryLotResponse;
  enableLowStockHighlight: boolean;
};

const StockQuantity: React.FC<StockQuantityProps> = ({ record, enableLowStockHighlight }) => {
  const { create } = useNavigation();

  const incrementQuantityHandler = () => {
    create('purchases');
  };
  const decrementQuantityHandler = () => {
    create('sales');
  };

  const { data: canAccessSaleCreate } = useCan({
    resource: 'sales',
    action: 'create',
  });

  const { data: canAccessPurchaseCreate } = useCan({
    resource: 'purchases',
    action: 'create',
  });

  const isLowStock =
    enableLowStockHighlight &&
    record.minUnit !== null &&
    record.minUnit !== undefined &&
    record.quantity < record.minUnit;

  return (
    <Space>
      <button
        className={styles['increment-button']}
        onClick={decrementQuantityHandler}
        disabled={!canAccessSaleCreate?.can}
      >
        <Icons.MinusCircleTwoTone />
      </button>
      <Text style={isLowStock ? { color: '#cf1322', fontWeight: 600 } : undefined}>
        {record?.quantity}
      </Text>
      <button
        className={styles['increment-button']}
        onClick={incrementQuantityHandler}
        disabled={!canAccessPurchaseCreate?.can}
      >
        <Icons.PlusCircleTwoTone />
      </button>
    </Space>
  );
};
